import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Button from './ui/Button';
import { parseWeekSeries } from '../utils/timeUtils';
import { previewDraftPublish } from '../services/apiService';

const PublishDraftModal = ({
    isOpen,
    onClose,
    onPublish,
    currentDraftName,
    timePeriods,
    scheduledInstances,
    selectedYearForView
}) => {
    const [publishMode, setPublishMode] = useState('all');
    const [selectedPeriodId, setSelectedPeriodId] = useState(null);
    const [customStartWeek, setCustomStartWeek] = useState('');
    const [customEndWeek, setCustomEndWeek] = useState('');
    const [isPublishing, setIsPublishing] = useState(false);
    const [warnings, setWarnings] = useState([]);
    const [activeTab, setActiveTab] = useState('stats');

    // Reset state when modal opens
    useEffect(() => {
        if (isOpen) {
            setPublishMode('all');
            setSelectedPeriodId(null);
            setCustomStartWeek('');
            setCustomEndWeek('');
            setWarnings([]);
            setActiveTab('stats');
        }
    }, [isOpen]);

    // Calculate the effective week range based on publish mode
    const effectiveWeekRange = useMemo(() => {
        if (publishMode === 'all') {
            return null;
        }

        if (publishMode === 'period' && selectedPeriodId) {
            const period = timePeriods.find(p => String(p.id) === String(selectedPeriodId));
            if (period && period.start_week && period.end_week) {
                return { startWeek: period.start_week, endWeek: period.end_week };
            }
        }

        if (publishMode === 'custom') {
            const start = parseInt(customStartWeek, 10);
            const end = parseInt(customEndWeek, 10);
            if (!isNaN(start) && !isNaN(end) && start >= 1 && start <= 53 && end >= 1 && end <= 53) {
                return { startWeek: start, endWeek: end };
            }
        }

        return null;
    }, [publishMode, selectedPeriodId, customStartWeek, customEndWeek, timePeriods]);

    // Helper to check if entry's first week is in range
    const isEntryInRange = useCallback((entry, startWeek, endWeek) => {
        if (!entry.weekSeries) return false;
        try {
            const weeks = parseWeekSeries(entry.weekSeries);
            if (weeks.length === 0) return false;
            const firstWeek = Math.min(...weeks);
            return startWeek <= endWeek
                ? firstWeek >= startWeek && firstWeek <= endWeek
                : firstWeek >= startWeek || firstWeek <= endWeek;
        } catch {
            return false;
        }
    }, []);

    // State for server preview data
    const [previewData, setPreviewData] = useState(null);
    const [isLoadingPreview, setIsLoadingPreview] = useState(false);

    // Fetch preview data from server when parameters change
    useEffect(() => {
        const fetchPreview = async () => {
            if (!isOpen || !currentDraftName || !selectedYearForView) {
                setPreviewData(null);
                return;
            }

            setIsLoadingPreview(true);
            try {
                const data = await previewDraftPublish(
                    currentDraftName,
                    selectedYearForView,
                    effectiveWeekRange
                );

                setPreviewData(data);
            } catch (error) {
                console.error('Error fetching preview data:', error);
                setPreviewData(null);
            } finally {
                setIsLoadingPreview(false);
            }
        };

        fetchPreview();
    }, [isOpen, currentDraftName, selectedYearForView, effectiveWeekRange]);

    // Use server preview data for change analysis
    const changeAnalysis = useMemo(() => {
        if (!previewData) {
            return {
                stats: {
                    liveBeforeCount: 0,
                    liveAfterCount: 0,
                    draftEntriesCount: 0,
                    addedCount: 0,
                    removedCount: 0,
                    modifiedCount: 0,
                    unchangedCount: 0
                },
                added: [],
                removed: [],
                modified: [],
                unchanged: []
            };
        }

        return {
            stats: {
                liveBeforeCount: previewData.stats.liveBeforeCount,
                liveAfterCount: previewData.stats.liveAfterCount,
                draftEntriesCount: previewData.stats.draftEntriesCount,
                addedCount: previewData.changes.added.length,
                removedCount: previewData.changes.removed.length,
                modifiedCount: previewData.changes.modified.length,
                unchangedCount: previewData.changes.unchanged.length
            },
            added: previewData.changes.added,
            removed: previewData.changes.removed,
            modified: previewData.changes.modified,
            unchanged: previewData.changes.unchanged
        };
    }, [previewData]);

    useEffect(() => {
        if (!effectiveWeekRange || !scheduledInstances.length) {
            setWarnings([]);
            return;
        }

        const newWarnings = [];
        const { startWeek, endWeek } = effectiveWeekRange;

        const isWeekInRange = (week) => {
            if (startWeek <= endWeek) {
                return week >= startWeek && week <= endWeek;
            } else {
                return week >= startWeek || week <= endWeek;
            }
        };

        const yearEntries = scheduledInstances.filter(
            entry => entry.isScheduled && entry.isScheduled() && entry.year === selectedYearForView
        );

        yearEntries.forEach(entry => {
            if (!entry.weekSeries) return;

            try {
                const weeks = parseWeekSeries(entry.weekSeries);
                if (weeks.length === 0) return;

                const firstWeek = Math.min(...weeks);
                const weeksInRange = weeks.filter(w => isWeekInRange(w));

                if (weeksInRange.length > 0 && !isWeekInRange(firstWeek)) {
                    const entryName = entry.displayName || entry.course || entry.title || `Entry ${entry.id}`;
                    newWarnings.push(
                        `"${entryName}" starts at week ${firstWeek} (outside range) but has ${weeksInRange.length} week(s) in the selected range`
                    );
                }
            } catch (error) {
                console.error('Error parsing week series for entry:', entry.id, error);
            }
        });

        setWarnings(newWarnings);
    }, [effectiveWeekRange, scheduledInstances, selectedYearForView]);

    const handlePublish = async () => {
        setIsPublishing(true);
        try {
            await Promise.resolve(onPublish(effectiveWeekRange));
            onClose();
        } catch (error) {
            console.error('Error publishing draft:', error);
        } finally {
            setIsPublishing(false);
        }
    };

    const isValidSelection = () => {
        if (publishMode === 'all') return true;
        if (publishMode === 'period') return selectedPeriodId !== null;
        if (publishMode === 'custom') {
            const start = parseInt(customStartWeek, 10);
            const end = parseInt(customEndWeek, 10);
            return !isNaN(start) && !isNaN(end) && start >= 1 && start <= 53 && end >= 1 && end <= 53;
        }
        return false;
    };

    if (!isOpen) return null;

    const { stats, added, removed, modified } = changeAnalysis;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full h-full max-w-7xl max-h-[95vh] flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-xl font-semibold text-gray-900">Publish Draft to Live</h2>
                            <p className="text-sm text-gray-600 mt-1">
                                Draft: <span className="font-medium">{currentDraftName}</span> • Year: {selectedYearForView}
                            </p>
                        </div>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600" title="Close" aria-label="Close">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Two Column Layout */}
                <div className="flex-1 flex min-h-0">
                    {/* Left: Controls */}
                    <div className="w-1/3 border-r border-gray-200 overflow-y-auto">
                        <div className="px-6 py-4 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Publishing Scope</label>
                                <div className="space-y-2">
                                    <label className="flex items-start p-2 border rounded cursor-pointer hover:bg-gray-50">
                                        <input type="radio" name="publishMode" value="all" checked={publishMode === 'all'} onChange={() => setPublishMode('all')} className="mr-2 mt-0.5" />
                                        <div className="flex-1">
                                            <div className="text-sm font-medium text-gray-900">Entire Draft</div>
                                            <div className="text-xs text-gray-500">All entries for this year</div>
                                        </div>
                                    </label>

                                    <label className="flex items-start p-2 border rounded cursor-pointer hover:bg-gray-50">
                                        <input type="radio" name="publishMode" value="period" checked={publishMode === 'period'} onChange={() => setPublishMode('period')} className="mr-2 mt-0.5" />
                                        <div className="flex-1">
                                            <div className="text-sm font-medium text-gray-900">Specific Period</div>
                                            <div className="text-xs text-gray-500 mb-2">Entries from a selected period</div>
                                            {publishMode === 'period' && (
                                                <select value={selectedPeriodId || ''} onChange={(e) => setSelectedPeriodId(e.target.value)} className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500" onClick={(e) => e.stopPropagation()}>
                                                    <option value="">Select period...</option>
                                                    {timePeriods.map(period => (
                                                        <option key={period.id} value={period.id}>{period.name} (W{period.start_week}-{period.end_week})</option>
                                                    ))}
                                                </select>
                                            )}
                                        </div>
                                    </label>

                                    <label className="flex items-start p-2 border rounded cursor-pointer hover:bg-gray-50">
                                        <input type="radio" name="publishMode" value="custom" checked={publishMode === 'custom'} onChange={() => setPublishMode('custom')} className="mr-2 mt-0.5" />
                                        <div className="flex-1">
                                            <div className="text-sm font-medium text-gray-900">Custom Week Range</div>
                                            <div className="text-xs text-gray-500 mb-2">Enter week numbers (1-53)</div>
                                            {publishMode === 'custom' && (
                                                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                                    <input type="number" min="1" max="53" value={customStartWeek} onChange={(e) => setCustomStartWeek(e.target.value)} placeholder="Start" className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500" />
                                                    <span className="text-gray-500 text-xs">to</span>
                                                    <input type="number" min="1" max="53" value={customEndWeek} onChange={(e) => setCustomEndWeek(e.target.value)} placeholder="End" className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500" />
                                                </div>
                                            )}
                                        </div>
                                    </label>
                                </div>
                            </div>

                            {warnings.length > 0 && (
                                <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                                    <div className="flex items-start">
                                        <svg className="w-4 h-4 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                        <div className="flex-1">
                                            <h4 className="text-xs font-medium text-yellow-800 mb-1">{warnings.length} Warning{warnings.length > 1 ? 's' : ''}</h4>
                                            <ul className="text-xs text-yellow-700 space-y-0.5">
                                                {warnings.slice(0, 3).map((warning, index) => (
                                                    <li key={index} className="flex items-start"><span className="mr-1">•</span><span className="line-clamp-2">{warning}</span></li>
                                                ))}
                                                {warnings.length > 3 && <li className="text-yellow-600 italic text-xs">+{warnings.length - 3} more...</li>}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="bg-blue-50 border border-blue-200 rounded p-3">
                                <h4 className="text-xs font-medium text-blue-900 mb-1.5">Publishing Process</h4>
                                <ul className="text-xs text-blue-800 space-y-0.5">
                                    <li>• Draft saved automatically</li>
                                    <li>• Live backup created</li>
                                    <li>• {effectiveWeekRange ? `Publishes weeks ${effectiveWeekRange.startWeek}-${effectiveWeekRange.endWeek}` : 'Publishes all entries'}</li>
                                    <li>• Stay in draft mode after</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Right: Visualization */}
                    <div className="w-2/3 flex flex-col">
                        <div className="flex border-b border-gray-200 flex-shrink-0">
                            <button onClick={() => setActiveTab('stats')} className={`px-4 py-3 text-sm font-medium border-b-2 ${activeTab === 'stats' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Overview</button>
                            <button onClick={() => setActiveTab('added')} className={`px-4 py-3 text-sm font-medium border-b-2 ${activeTab === 'added' ? 'border-green-600 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Added ({stats.addedCount})</button>
                            <button onClick={() => setActiveTab('removed')} className={`px-4 py-3 text-sm font-medium border-b-2 ${activeTab === 'removed' ? 'border-red-600 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Removed ({stats.removedCount})</button>
                            <button onClick={() => setActiveTab('modified')} className={`px-4 py-3 text-sm font-medium border-b-2 ${activeTab === 'modified' ? 'border-orange-600 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Modified ({stats.modifiedCount})</button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6">
                            {isLoadingPreview ? (
                                <div className="flex items-center justify-center h-64">
                                    <div className="text-center">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                                        <p className="text-sm text-gray-600">Calculating changes...</p>
                                    </div>
                                </div>
                            ) : activeTab === 'stats' && (
                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold text-gray-900">Change Summary</h3>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="bg-gray-50 rounded-lg p-4">
                                            <div className="text-sm text-gray-600 mb-1">Live Schedule (Before)</div>
                                            <div className="text-3xl font-bold text-gray-900">{stats.liveBeforeCount}</div>
                                            <div className="text-xs text-gray-500 mt-1">entries</div>
                                        </div>
                                        <div className="bg-gray-50 rounded-lg p-4">
                                            <div className="text-sm text-gray-600 mb-1">Live Schedule (After)</div>
                                            <div className="text-3xl font-bold text-gray-900">{stats.liveAfterCount}</div>
                                            <div className="text-xs text-gray-500 mt-1">
                                                {stats.liveAfterCount > stats.liveBeforeCount ? <span className="text-green-600">+{stats.liveAfterCount - stats.liveBeforeCount}</span> : stats.liveAfterCount < stats.liveBeforeCount ? <span className="text-red-600">{stats.liveAfterCount - stats.liveBeforeCount}</span> : <span>no change</span>}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="border-t border-gray-200 pt-4 space-y-3">
                                        <div className="flex items-center justify-between p-3 bg-green-50 rounded">
                                            <span className="text-sm font-medium text-green-900">Entries Added</span>
                                            <span className="text-2xl font-bold text-green-600">{stats.addedCount}</span>
                                        </div>
                                        <div className="flex items-center justify-between p-3 bg-red-50 rounded">
                                            <span className="text-sm font-medium text-red-900">Entries Removed</span>
                                            <span className="text-2xl font-bold text-red-600">{stats.removedCount}</span>
                                        </div>
                                        <div className="flex items-center justify-between p-3 bg-orange-50 rounded">
                                            <span className="text-sm font-medium text-orange-900">Entries Modified</span>
                                            <span className="text-2xl font-bold text-orange-600">{stats.modifiedCount}</span>
                                        </div>
                                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                                            <span className="text-sm font-medium text-gray-700">Unchanged</span>
                                            <span className="text-2xl font-bold text-gray-600">{stats.unchangedCount}</span>
                                        </div>
                                    </div>
                                    {effectiveWeekRange && (
                                        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
                                            <p className="text-sm text-blue-900"><strong>Publishing Range:</strong> Weeks {effectiveWeekRange.startWeek}-{effectiveWeekRange.endWeek}</p>
                                            <p className="text-xs text-blue-700 mt-1">Only courses starting in this range will be affected. Other Live entries remain unchanged.</p>
                                        </div>
                                    )}
                                    {previewData && (
                                        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded">
                                            <p className="text-sm text-green-900"><strong>Server Preview:</strong> This preview is generated by the server using the exact same logic that will be used during publishing.</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {!isLoadingPreview && activeTab === 'added' && (
                                <div className="space-y-2">
                                    <h3 className="text-lg font-semibold text-green-900 mb-3">New Entries ({added.length})</h3>
                                    {added.length === 0 ? <p className="text-sm text-gray-500 italic">No entries will be added</p> : added.map(entry => (
                                        <div key={entry.id} className="p-3 bg-green-50 border border-green-200 rounded">
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <div className="text-sm font-medium text-green-900">{entry.name}</div>
                                                    <div className="text-xs text-green-700 mt-1">{entry.details}</div>
                                                </div>
                                                {entry.firstWeek && <div className="ml-2 px-2 py-1 bg-green-100 rounded text-xs font-mono text-green-800">W{entry.firstWeek}</div>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {!isLoadingPreview && activeTab === 'removed' && (
                                <div className="space-y-2">
                                    <h3 className="text-lg font-semibold text-red-900 mb-3">Removed Entries ({removed.length})</h3>
                                    {removed.length === 0 ? <p className="text-sm text-gray-500 italic">No entries will be removed</p> : removed.map(entry => (
                                        <div key={entry.id} className="p-3 bg-red-50 border border-red-200 rounded">
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <div className="text-sm font-medium text-red-900">{entry.name}</div>
                                                    <div className="text-xs text-red-700 mt-1">{entry.details}</div>
                                                </div>
                                                {entry.firstWeek && <div className="ml-2 px-2 py-1 bg-red-100 rounded text-xs font-mono text-red-800">W{entry.firstWeek}</div>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {!isLoadingPreview && activeTab === 'modified' && (
                                <div className="space-y-2">
                                    <h3 className="text-lg font-semibold text-orange-900 mb-3">Modified Entries ({modified.length})</h3>
                                    {modified.length === 0 ? <p className="text-sm text-gray-500 italic">No entries will be modified</p> : modified.map(entry => (
                                        <div key={entry.id} className="p-3 bg-orange-50 border border-orange-200 rounded">
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <div className="text-sm font-medium text-orange-900">{entry.name}</div>
                                                    <div className="text-xs text-orange-700 mt-1">{entry.details}</div>
                                                </div>
                                                {entry.firstWeek && <div className="ml-2 px-2 py-1 bg-orange-100 rounded text-xs font-mono text-orange-800">W{entry.firstWeek}</div>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center flex-shrink-0">
                    <div className="text-sm text-gray-600">{stats.addedCount + stats.removedCount + stats.modifiedCount} total changes</div>
                    <div className="flex gap-3">
                        <Button onClick={onClose} variant="secondary" disabled={isPublishing} title="Cancel" aria-label="Cancel">Cancel</Button>
                        <Button onClick={handlePublish} variant="primary" disabled={!isValidSelection() || isPublishing || isLoadingPreview} className="bg-green-600 hover:bg-green-700" title={isPublishing ? 'Publishing...' : isLoadingPreview ? 'Calculating changes...' : 'Publish to Live'} aria-label={isPublishing ? 'Publishing' : isLoadingPreview ? 'Calculating changes' : 'Publish to Live'}>
                            {isPublishing ? 'Publishing...' : isLoadingPreview ? 'Calculating...' : 'Publish to Live'}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PublishDraftModal;

